import Tooltip from '@mui/material/Tooltip'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import { useColorScheme } from '@mui/material/styles'
import BrightnessAutoIcon from '@mui/icons-material/BrightnessAuto'
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined'
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined'

/**
 * Which scheme the surface is actually drawn in, right now.
 *
 * **Three settings, two answers.** `mode` is what the reader chose and
 * may be `system`; `colorScheme` is what that resolves to once the
 * operating system has been asked. Anything that has to *match* the
 * surface — a control this theme does not reach, an embedded editor
 * with a skin of its own — wants the second, and asking for the first
 * is how something ends up light inside a dark tool.
 *
 * It is `undefined` for exactly one render, before the provider has
 * read storage. Callers treat that as light, which is the default.
 */
export function useIsDark(): boolean {
  const { colorScheme } = useColorScheme()
  return colorScheme === 'dark'
}

/**
 * The appearance control: system, light, dark.
 *
 * It sits with the account rather than in a settings screen because it
 * is a property of *this reader at this desk* and not of the site being
 * edited — a settings page would file it with the site's hostnames and
 * its search metadata, which are the tenant's and travel with it.
 *
 * Icons rather than words, because it is three mutually exclusive
 * states in a 268px column and the words for them ("System", "Light",
 * "Dark") are longer than the control. Each carries its name in a
 * tooltip and in `aria-label`, so nothing about it is icon-only to a
 * screen reader.
 */
export function Appearance() {
  const { mode, setMode } = useColorScheme()

  // One render before storage is read. Drawing the group with nothing
  // selected for that frame is a visible flicker of an unset control;
  // `system` is what the provider is about to say anyway.
  const current = mode ?? 'system'

  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={current}
      onChange={(_event, next: 'system' | 'light' | 'dark' | null) => {
        // Null is the group reporting that the selected button was
        // clicked again. There is no "no appearance", so it is ignored
        // rather than allowed to unset the theme.
        if (next) setMode(next)
      }}
      aria-label="Appearance"
    >
      <Tooltip title="Match the system">
        <ToggleButton value="system" aria-label="Match the system" sx={{ px: 1, py: 0.25 }}>
          <BrightnessAutoIcon fontSize="small" />
        </ToggleButton>
      </Tooltip>
      <Tooltip title="Light">
        <ToggleButton value="light" aria-label="Light" sx={{ px: 1, py: 0.25 }}>
          <LightModeOutlinedIcon fontSize="small" />
        </ToggleButton>
      </Tooltip>
      <Tooltip title="Dark">
        <ToggleButton value="dark" aria-label="Dark" sx={{ px: 1, py: 0.25 }}>
          <DarkModeOutlinedIcon fontSize="small" />
        </ToggleButton>
      </Tooltip>
    </ToggleButtonGroup>
  )
}
